import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AddOrdersModal = ({ order, onClose, onSuccess }) => {
  const [userFullName, setUserFullName] = useState('');
  const [orders, setOrders] = useState([]);

  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?._id;

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}user-details`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("jwt")}` },
        });

        const fullName = `${res.data.FirstName || ''} ${res.data.LastName || ''}`.trim();
        setUserFullName(fullName);

        if (order) {
          setOrders([{
            userName: order.UserName || fullName,
            clientName: order.Client || '',
            orderDate: order.Date?.split('T')[0] || '',
            orderValue: order.Value || '',
            userComment: order.Comment || '',
            userStatus: order.Status || '',
            isCustomStatus: !['Pending', 'Confirmed', 'Completed'].includes(order.Status),
          }]);
        } else {
          setOrders([{
            userName: fullName,
            clientName: '',
            orderDate: '',
            orderValue: '',
            userComment: '',
            userStatus: '',
            isCustomStatus: false,
          }]);
        }
      } catch (err) {
        console.error("Error fetching user details:", err);
        alert("Failed to load user info.");
      }
    };

    fetchUserDetails();
  }, [order]);

  const handleOrderChange = (index, field, value) => {
    const updated = [...orders];
    updated[index][field] = value;
    setOrders(updated);
  };

  const handleStatusChange = (index, value) => {
    const updated = [...orders];
    updated[index].isCustomStatus = value === 'Other';
    updated[index].userStatus = value === 'Other' ? '' : value;
    setOrders(updated);
  };

  const addRow = () => {
    setOrders([...orders, {
      userName: userFullName,
      clientName: '',
      orderDate: '',
      orderValue: '',
      userComment: '',
      userStatus: '',
      isCustomStatus: false,
    }]);
  };

  const removeRow = (index) => {
    setOrders(orders.filter((_, i) => i !== index));
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  const formattedData = orders.map(({ isCustomStatus, ...rest }) => ({
    Client: rest.clientName?.trim(),
    UserName: rest.userName?.trim(),
    Date: rest.orderDate,
    Value: Number(rest.orderValue),
    Comment: rest.userComment?.trim(),
    Status: rest.userStatus?.trim(),
  }));

  try {
    const config = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("jwt")}`,
      }
    };

    if (order) {
      // EDIT mode
      await axios.put(`${process.env.REACT_APP_API_URL}edit-order/${order._id}`, {
        data: formattedData[0],
      }, config);
    } else {
      // ADD mode
      await axios.post(`${process.env.REACT_APP_API_URL}add-orders/${userId}`, formattedData, config);
    }

    onSuccess();
  } catch (err) {
    console.error("🚨 Submit failed:", err);
    alert("Submission failed. Check your input.");
  }
};


  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 px-4">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 rounded-2xl shadow-2xl">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          {order ? 'Edit Order' : 'Add New Orders'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {orders.map((orderItem, index) => (
            <div
              key={index}
              className="border border-gray-300 rounded-xl p-4 bg-gray-50 relative shadow-sm"
            >
              {!order && (
              <h3 className="text-lg font-semibold mb-4 text-gray-700">
                Order #{index + 1}
              </h3>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600">User Name</label>
                  <input
                    type="text"
                    value={orderItem.userName}
                    disabled
                    className="w-full px-3 py-2 mt-1 border rounded bg-gray-100 text-gray-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600">Client Name</label>
                  <input
                    type="text"
                    placeholder="Client Name"
                    value={orderItem.clientName}
                    onChange={(e) => handleOrderChange(index, 'clientName', e.target.value)}
                    className="w-full px-3 py-2 mt-1 border rounded"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600">Order Date</label>
                  <input
                    type="date"
                    value={orderItem.orderDate}
                    onChange={(e) => handleOrderChange(index, 'orderDate', e.target.value)}
                    className="w-full px-3 py-2 mt-1 border rounded"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600">Order Value (₹)</label>
                  <input
                    type="number"
                    placeholder="Order Value"
                    value={orderItem.orderValue}
                    onChange={(e) => handleOrderChange(index, 'orderValue', e.target.value)}
                    className="w-full px-3 py-2 mt-1 border rounded"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600">Comment</label>
                  <input
                    type="text"
                    placeholder="Optional comment"
                    value={orderItem.userComment}
                    onChange={(e) => handleOrderChange(index, 'userComment', e.target.value)}
                    className="w-full px-3 py-2 mt-1 border rounded"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600">Status</label>
                  <select
                    value={orderItem.isCustomStatus ? 'Other' : orderItem.userStatus}
                    onChange={(e) => handleStatusChange(index, e.target.value)}
                    className="w-full px-3 py-2 mt-1 border rounded"
                    required
                  >
                    <option value="">Select Status</option>
                    <option value="Order Received">Order Received</option>
                    <option value="PI Sent">PI Sent</option>
                    <option value="Payment Pending">Payment Pending</option>
                    <option value="Dispatch Clearance Pending">Dispatch Clearance Pending</option>
                    <option value="Not in Stock">Not in Stock</option>
                    <option value="Order Complete">Order Complete</option>
                    <option value="Part Clearance Received">Part Clearance Received</option>
                    <option value="Other">Other (type manually)</option>
                  </select>

                  {orderItem.isCustomStatus && (
                    <input
                      type="text"
                      placeholder="Custom Status"
                      value={orderItem.userStatus}
                      onChange={(e) => handleOrderChange(index, 'userStatus', e.target.value)}
                      className="w-full px-3 py-2 mt-2 border rounded"
                      required
                    />
                  )}
                </div>
              </div>

              {!order && orders.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeRow(index)}
                  className="absolute bg-red-600 top-3 right-3 text-white text-sm hover:underline hover:bg-red-800"
                >
                  Remove
                </button>
              )}
            </div>
          ))}

          {!order && (
            <div className="flex justify-start">
              <button
                type="button"
                onClick={addRow}
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-800"
              >
                + Add Another Row
              </button>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
            >
              {order ? 'Update Order' : 'Submit Orders'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddOrdersModal;
